import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { publicService } from '@/api/services/public.service';
import { adminService } from '@/api/services/admin.service';
import type { UpdateSiteSettingsRequest } from '@/api/types';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';

// Public hook for fetching site settings
export const useSiteSettings = () => {
  return useQuery({
    queryKey: ['site-settings'],
    queryFn: () => publicService.getSiteSettings(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Admin hook for managing site settings
export const useAdminSiteSettings = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-site-settings'],
    queryFn: () => adminService.getSiteSettings(),
  });

  const updateSettings = useMutation({
    mutationFn: (data: UpdateSiteSettingsRequest) => adminService.updateSiteSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-site-settings'] });
      queryClient.invalidateQueries({ queryKey: ['site-settings'] });
      toast({
        title: t('settings.notifications.updated') || 'Settings updated',
        description: t('settings.notifications.updatedDesc') || 'Site settings have been updated successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('common.error') || 'Error',
        description: error.message || t('settings.notifications.updateFailed') || 'Failed to update settings.',
        variant: 'destructive',
      });
    },
  });

  const uploadLogo = useMutation({
    mutationFn: (file: File) => adminService.uploadLogo(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-site-settings'] });
      queryClient.invalidateQueries({ queryKey: ['site-settings'] });
      toast({
        title: t('settings.notifications.logoUploaded') || 'Logo uploaded',
        description: t('settings.notifications.logoUploadedDesc') || 'Logo has been uploaded successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('common.error') || 'Error',
        description: error.message || t('settings.notifications.logoUploadFailed') || 'Failed to upload logo.',
        variant: 'destructive',
      });
    },
  });

  const uploadLogoDark = useMutation({
    mutationFn: (file: File) => adminService.uploadLogoDark(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-site-settings'] });
      queryClient.invalidateQueries({ queryKey: ['site-settings'] });
      toast({
        title: t('settings.notifications.darkLogoUploaded') || 'Dark logo uploaded',
        description: t('settings.notifications.darkLogoUploadedDesc') || 'Dark logo has been uploaded successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('common.error') || 'Error',
        description: error.message || t('settings.notifications.darkLogoUploadFailed') || 'Failed to upload dark logo.',
        variant: 'destructive',
      });
    },
  });

  const uploadFavicon = useMutation({
    mutationFn: (file: File) => adminService.uploadFavicon(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-site-settings'] });
      queryClient.invalidateQueries({ queryKey: ['site-settings'] });
      toast({
        title: t('settings.notifications.faviconUploaded') || 'Favicon uploaded',
        description: t('settings.notifications.faviconUploadedDesc') || 'Favicon has been uploaded successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('common.error') || 'Error',
        description: error.message || t('settings.notifications.faviconUploadFailed') || 'Failed to upload favicon.',
        variant: 'destructive',
      });
    },
  });

  const deleteLogo = useMutation({
    mutationFn: () => adminService.deleteLogo(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-site-settings'] });
      queryClient.invalidateQueries({ queryKey: ['site-settings'] });
      toast({
        title: t('settings.notifications.logoDeleted') || 'Logo deleted',
        description: t('settings.notifications.logoDeletedDesc') || 'Logo has been deleted successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('common.error') || 'Error',
        description: error.message || t('settings.notifications.logoDeleteFailed') || 'Failed to delete logo.',
        variant: 'destructive',
      });
    },
  });

  return {
    settings: data?.data,
    isLoading,
    error,
    updateSettings: updateSettings.mutate,
    isUpdating: updateSettings.isPending,
    uploadLogo: uploadLogo.mutate,
    isUploadingLogo: uploadLogo.isPending,
    uploadLogoDark: uploadLogoDark.mutate,
    isUploadingLogoDark: uploadLogoDark.isPending,
    uploadFavicon: uploadFavicon.mutate,
    isUploadingFavicon: uploadFavicon.isPending,
    deleteLogo: deleteLogo.mutate,
    isDeletingLogo: deleteLogo.isPending,
  };
};
